/**
 * Utility functions to transform viz_data format to EnhancedBarChart, EnhancedLineChart, EnhancedAreaChart, and BrandPriceLadder format
 */

import { VizData as BarChartVizData } from '../components/molecules/EnhancedBarChart/EnhancedBarChart.types';
import { BrandPriceLadderVizData } from '../components/molecules/BrandPriceLadder/BrandPriceLadder.types';
import { MetricId } from '../components/molecules/EnhancedBarChart/useChartFilters';

// Extended VizData type that includes Area chart type
type VizData = Omit<BarChartVizData, 'chart_type'> & {
    chart_type: 'Bar' | 'Line' | 'Area';
};

/**
 * Returns true if viz_data represents a loading/skeleton state (e.g. _loading: true).
 * Such viz_data should render a skeleton placeholder instead of the chart.
 */
export function isVizDataLoadingState(vizData: any): boolean {
    return !!(vizData && typeof vizData === 'object' && vizData._loading === true);
}

/**
 * Returns true if viz_data represents an error state (e.g. "Error: Could not generate visualization").
 * Such viz_data should not be rendered as a chart/table.
 */
export function isVizDataErrorState(vizData: any): boolean {
    if (!vizData || typeof vizData !== 'object') return false;

    // chart_type is explicitly 'error'
    const chartType = String(vizData.chart_type ?? '').toLowerCase();
    if (chartType === 'error') return true;

    // chartTitle indicates error (e.g. "Error: Could not generate visualization")
    const chartTitle = String(vizData.chartTitle ?? '');
    if (
        chartTitle.toLowerCase().includes('error: could not generate') ||
        chartTitle.toLowerCase().includes('could not generate visualization')
    )
        return true;

    // xAxisTitle contains "Error" as placeholder (e.g. xAxisTitle: ["Error"])
    const xAxis = vizData.xAxisTitle ?? vizData.xAxisTitles;
    if (Array.isArray(xAxis) && xAxis.length > 0 && String(xAxis[0]).toLowerCase() === 'error') return true;

    // data has error placeholder: x: ["Error"], y: [[0]]
    const data = vizData.data;
    if (Array.isArray(data) && data.length > 0) {
        const first = data[0];
        const x = first?.x;
        const y = first?.y;
        if (
            Array.isArray(x) &&
            x.length === 1 &&
            String(x[0]).toLowerCase() === 'error' &&
            Array.isArray(y) &&
            y.length > 0 &&
            Array.isArray(y[0]) &&
            y[0].length === 1 &&
            y[0][0] === 0
        ) {
            return true;
        }
    }

    return false;
}

export interface TransformedChartData {
    data: Array<Record<string, any>>;
    xAxisKey: string;
    y1BarConfigs: Array<{
        dataKey: string;
        color: string;
        label: string;
    }>;
    y2BarConfigs: Array<{
        dataKey: string;
        color: string;
        label: string;
    }>;
    title: string;
    yAxisLabel?: string;
    /** Primary (y1) axis caption when using viz_data with metric families */
    y1AxisLabel?: string;
    /** Secondary (y2) axis caption when using viz_data with metric families */
    y2AxisLabel?: string;
    zAxisKey?: string;
}

export interface TransformedLineChartData {
    data: Array<Record<string, any>>;
    xAxisKey: string;
    y1LineConfigs: Array<{
        dataKey: string;
        color: string;
        label: string;
    }>;
    y2LineConfigs: Array<{
        dataKey: string;
        color: string;
        label: string;
    }>;
    title: string;
    yAxisLabel?: string;
    y1AxisLabel?: string;
    y2AxisLabel?: string;
    zAxisKey?: string;
}

export interface TransformedAreaChartData {
    data: Array<Record<string, any>>;
    xAxisKey: string;
    y1AreaConfigs: Array<{
        dataKey: string;
        color: string;
        label: string;
    }>;
    y2AreaConfigs: Array<{
        dataKey: string;
        color: string;
        label: string;
    }>;
    title: string;
    yAxisLabel?: string;
    y1AxisLabel?: string;
    y2AxisLabel?: string;
    zAxisKey?: string;
}

// Default color palette for chart series
/**
 * Accept only a non-empty array of scalars as headers; anything else is treated as absent
 * so charts keep pre-headers behavior (axis titles from series / first metric).
 */
function normalizeYAxisHeaders(raw: unknown): string[] | undefined {
    if (!Array.isArray(raw) || raw.length === 0) return undefined;
    const out: string[] = [];
    for (const x of raw) {
        if (x != null && (typeof x === 'string' || typeof x === 'number')) {
            out.push(String(x));
        } else if (x != null && typeof x === 'boolean') {
            out.push(String(x));
        } else {
            // Preserve index alignment for sparse/wrongly typed slots
            out.push('');
        }
    }
    return out.some((s) => String(s).trim() !== '') ? out : undefined;
}

/**
 * When multiple metrics from the same y-axis family are selected, use `yAxisHeaders[family]`
 * as the axis title instead of the first series name. Single selection keeps the metric label.
 * If headers are missing or invalid, behavior matches legacy (first selected series label).
 */
function resolveAxisLabelForMetricSelection(
    metricIds: MetricId[] | undefined,
    seriesConfigs: Array<{ label: string }>,
    yAxisHeaders?: string[],
): string | undefined {
    if (!metricIds?.length || !seriesConfigs.length) return undefined;

    const familyIndices = metricIds
        .map((id) => Number(id.split('_')[0]))
        .filter((f) => Number.isFinite(f) && f >= 0);
    if (familyIndices.length !== metricIds.length) return seriesConfigs[0]?.label;

    const uniqueFamilies = [...new Set(familyIndices)];

    if (metricIds.length > 1 && uniqueFamilies.length === 1 && yAxisHeaders) {
        const h = yAxisHeaders[uniqueFamilies[0]];
        if (h != null && String(h).trim() !== '') return String(h);
    }

    if (metricIds.length > 1 && uniqueFamilies.length > 1 && yAxisHeaders) {
        const parts = uniqueFamilies.map((f) => yAxisHeaders[f]).filter((h) => h != null && String(h).trim() !== '');
        // Avoid partial titles if some families lack headers — fall back to legacy label
        if (parts.length === uniqueFamilies.length) return parts.map(String).join(' / ');
    }

    return seriesConfigs[0]?.label;
}

/**
 * When xAxisType is 'Category', sort data points by the primary y metric (y[0][0]) descending.
 * Groups by x-value and aggregates when multiple rows share the same x (e.g. Z-axis facets).
 */
function sortRawDataForCategoryAxis(
    rawData: Array<{ x: string[]; y: number[][]; z?: string[] }>,
    xAxisType: string[] | undefined,
): Array<{ x: string[]; y: number[][]; z?: string[] }> {
    if (xAxisType?.[0] !== 'Category') return rawData;

    const xAggregates = new Map<string, number>();
    for (const dp of rawData) {
        const xVal = dp.x[0] || '';
        const yVal = dp.y[0]?.[0] ?? 0;
        xAggregates.set(xVal, (xAggregates.get(xVal) ?? 0) + yVal);
    }

    return [...rawData].sort((a, b) => {
        const aVal = xAggregates.get(a.x[0] || '') ?? 0;
        const bVal = xAggregates.get(b.x[0] || '') ?? 0;
        return bVal - aVal;
    });
}

const DEFAULT_COLORS = [
    '#5B50D1', // Purple
    '#DBC7A4', // Beige
    '#4ECDC4', // Teal
    '#FF6B6B', // Red
    '#95E1D3', // Mint
    '#F38181', // Pink
    '#AA96DA', // Lavender
    '#FCBAD3', // Light Pink
    '#A8D8EA', // Sky Blue
    '#FFD93D', // Yellow
];

/** Coerces a value to string[]: passes arrays through, wraps a single primitive, returns [] for null/undefined. */
function toStringArray(value: unknown): string[] {
    if (Array.isArray(value)) return value as string[];
    if (value != null && value !== '') return [String(value)];
    return [];
}

/** Bar/Line/Area viz_data: accept `xAxisTitles` (LLM plural) and tolerate missing titles. */
function normalizeChartVizXAxisTitles(vizData: VizData): string[] {
    const raw = vizData as VizData & { xAxisTitles?: string[] };
    return toStringArray(raw.xAxisTitle ?? raw.xAxisTitles);
}

/**
 * Transforms viz_data format to EnhancedBarChart format
 * @param vizData - The visualization data
 * @param selectedMetrics - Object with y1 and y2 metric IDs (format: 'familyIndex_metricIndex')
 */
export function transformVizDataToBarChart(
    vizData: VizData,
    selectedMetrics?: { y1: MetricId[]; y2: MetricId[] },
): TransformedChartData {
    const { chartTitle, yAxisTitle, xAxisType, data: rawDataUnsorted, zAxisTitle, yAxisHeaders: rawYAxisHeaders } = vizData;
    const yAxisHeaders = normalizeYAxisHeaders(rawYAxisHeaders);
    const rawData = sortRawDataForCategoryAxis(rawDataUnsorted ?? [], xAxisType);

    // Determine x-axis key (use first xAxisTitle or default to 'x')
    const xAxisKey = normalizeChartVizXAxisTitles(vizData)[0] || 'x';

    // Determine z-axis key if present
    const zAxisKey = zAxisTitle?.[0];

    // Process selected metrics for y1 and y2
    const y1BarConfigs: Array<{ dataKey: string; color: string; label: string }> = [];
    const y2BarConfigs: Array<{ dataKey: string; color: string; label: string }> = [];
    let colorIndex = 0;

    // Helper to parse metric ID
    const parseMetricId = (metricId: MetricId): { familyIndex: number; metricIndex: number } | null => {
        if (!metricId) return null;
        const [familyIndex, metricIndex] = metricId.split('_').map(Number);
        return { familyIndex, metricIndex };
    };

    // Process y1 metrics (array of metric IDs)
    if (selectedMetrics?.y1 && selectedMetrics.y1.length > 0) {
        selectedMetrics.y1.forEach((metricId) => {
            const parsed = parseMetricId(metricId);
            if (parsed) {
                const { familyIndex, metricIndex } = parsed;
                const metricLabel = yAxisTitle?.[familyIndex]?.[metricIndex];
                if (metricLabel !== undefined) {
                    const dataKey = `y1_${familyIndex}_${metricIndex}`;
                    y1BarConfigs.push({
                        dataKey,
                        color: DEFAULT_COLORS[colorIndex % DEFAULT_COLORS.length],
                        label: metricLabel,
                    });
                    colorIndex++;
                }
            }
        });
    }

    // Process y2 metrics (array of metric IDs)
    if (selectedMetrics?.y2 && selectedMetrics.y2.length > 0) {
        selectedMetrics.y2.forEach((metricId) => {
            const parsed = parseMetricId(metricId);
            if (parsed) {
                const { familyIndex, metricIndex } = parsed;
                const metricLabel = yAxisTitle?.[familyIndex]?.[metricIndex];
                if (metricLabel !== undefined) {
                    const dataKey = `y2_${familyIndex}_${metricIndex}`;
                    y2BarConfigs.push({
                        dataKey,
                        color: DEFAULT_COLORS[colorIndex % DEFAULT_COLORS.length],
                        label: metricLabel,
                    });
                    colorIndex++;
                }
            }
        });
    }

    // Transform data points
    const transformedData: Array<Record<string, any>> = rawData.map((dataPoint, index) => {
        const record: Record<string, any> = {
            // X-axis value (use first x value or create index-based key)
            [xAxisKey]: dataPoint.x[0] || `Point ${index + 1}`,
        };

        // Add y1 values (multiple metrics)
        if (selectedMetrics?.y1 && selectedMetrics.y1.length > 0) {
            selectedMetrics.y1.forEach((metricId) => {
                const parsed = parseMetricId(metricId);
                if (parsed) {
                    const { familyIndex, metricIndex } = parsed;
                    const yValue = dataPoint.y[familyIndex]?.[metricIndex];
                    if (yValue !== undefined) {
                        record[`y1_${familyIndex}_${metricIndex}`] = yValue;
                    }
                }
            });
        }

        // Add y2 values (multiple metrics)
        if (selectedMetrics?.y2 && selectedMetrics.y2.length > 0) {
            selectedMetrics.y2.forEach((metricId) => {
                const parsed = parseMetricId(metricId);
                if (parsed) {
                    const { familyIndex, metricIndex } = parsed;
                    const yValue = dataPoint.y[familyIndex]?.[metricIndex];
                    if (yValue !== undefined) {
                        record[`y2_${familyIndex}_${metricIndex}`] = yValue;
                    }
                }
            });
        }

        // Add z-axis (dimension) values if present
        // Store all Z values and create a series identifier from all Z values
        if (zAxisTitle && dataPoint.z && dataPoint.z.length > 0) {
            // Store all Z values
            record.zValues = dataPoint.z;
            // Create a series identifier from all Z values (e.g., "Assoc Brands - AB Inputs")
            record.zSeriesKey = dataPoint.z.join('-');
            // Keep backward compatibility with zAxisKey (lowest level)
            if (zAxisKey) {
                record[zAxisKey] = dataPoint.z[dataPoint.z.length - 1]; // Use lowest level
            }
        }

        return record;
    });

    const y1AxisLabel = resolveAxisLabelForMetricSelection(selectedMetrics?.y1, y1BarConfigs, yAxisHeaders);
    const y2AxisLabel = resolveAxisLabelForMetricSelection(selectedMetrics?.y2, y2BarConfigs, yAxisHeaders);
    const yAxisLabel = y1AxisLabel || y2AxisLabel || yAxisTitle?.[0]?.[0] || 'Value';

    return {
        data: transformedData,
        xAxisKey,
        y1BarConfigs,
        y2BarConfigs,
        title: chartTitle,
        yAxisLabel,
        y1AxisLabel,
        y2AxisLabel,
        zAxisKey,
    };
}

/**
 * Transforms viz_data format to EnhancedLineChart format
 * @param vizData - The visualization data
 * @param selectedMetrics - Object with y1 and y2 metric IDs (format: 'familyIndex_metricIndex')
 */
export function transformVizDataToLineChart(
    vizData: VizData,
    selectedMetrics?: { y1: MetricId[]; y2: MetricId[] },
): TransformedLineChartData {
    const { chartTitle, yAxisTitle, xAxisType, data: rawDataUnsorted, zAxisTitle, yAxisHeaders: rawYAxisHeaders } = vizData;
    const yAxisHeaders = normalizeYAxisHeaders(rawYAxisHeaders);
    const rawData = sortRawDataForCategoryAxis(rawDataUnsorted ?? [], xAxisType);

    // Determine x-axis key (use first xAxisTitle or default to 'x')
    const xAxisKey = normalizeChartVizXAxisTitles(vizData)[0] || 'x';

    // Determine z-axis key if present
    const zAxisKey = zAxisTitle?.[0];

    // Process selected metrics for y1 and y2
    const y1LineConfigs: Array<{ dataKey: string; color: string; label: string }> = [];
    const y2LineConfigs: Array<{ dataKey: string; color: string; label: string }> = [];
    let colorIndex = 0;

    // Helper to parse metric ID
    const parseMetricId = (metricId: MetricId): { familyIndex: number; metricIndex: number } | null => {
        if (!metricId) return null;
        const [familyIndex, metricIndex] = metricId.split('_').map(Number);
        return { familyIndex, metricIndex };
    };

    // Process y1 metrics (array of metric IDs)
    if (selectedMetrics?.y1 && selectedMetrics.y1.length > 0) {
        selectedMetrics.y1.forEach((metricId) => {
            const parsed = parseMetricId(metricId);
            if (parsed) {
                const { familyIndex, metricIndex } = parsed;
                const metricLabel = yAxisTitle?.[familyIndex]?.[metricIndex];
                if (metricLabel !== undefined) {
                    const dataKey = `y1_${familyIndex}_${metricIndex}`;
                    y1LineConfigs.push({
                        dataKey,
                        color: DEFAULT_COLORS[colorIndex % DEFAULT_COLORS.length],
                        label: metricLabel,
                    });
                    colorIndex++;
                }
            }
        });
    }

    // Process y2 metrics (array of metric IDs)
    if (selectedMetrics?.y2 && selectedMetrics.y2.length > 0) {
        selectedMetrics.y2.forEach((metricId) => {
            const parsed = parseMetricId(metricId);
            if (parsed) {
                const { familyIndex, metricIndex } = parsed;
                const metricLabel = yAxisTitle?.[familyIndex]?.[metricIndex];
                if (metricLabel !== undefined) {
                    const dataKey = `y2_${familyIndex}_${metricIndex}`;
                    y2LineConfigs.push({
                        dataKey,
                        color: DEFAULT_COLORS[colorIndex % DEFAULT_COLORS.length],
                        label: metricLabel,
                    });
                    colorIndex++;
                }
            }
        });
    }

    // Transform data points
    const transformedData: Array<Record<string, any>> = rawData.map((dataPoint, index) => {
        const record: Record<string, any> = {
            // X-axis value (use first x value or create index-based key)
            [xAxisKey]: dataPoint.x[0] || `Point ${index + 1}`,
        };

        // Add y1 values (multiple metrics)
        if (selectedMetrics?.y1 && selectedMetrics.y1.length > 0) {
            selectedMetrics.y1.forEach((metricId) => {
                const parsed = parseMetricId(metricId);
                if (parsed) {
                    const { familyIndex, metricIndex } = parsed;
                    const yValue = dataPoint.y[familyIndex]?.[metricIndex];
                    if (yValue !== undefined) {
                        record[`y1_${familyIndex}_${metricIndex}`] = yValue;
                    }
                }
            });
        }

        // Add y2 values (multiple metrics)
        if (selectedMetrics?.y2 && selectedMetrics.y2.length > 0) {
            selectedMetrics.y2.forEach((metricId) => {
                const parsed = parseMetricId(metricId);
                if (parsed) {
                    const { familyIndex, metricIndex } = parsed;
                    const yValue = dataPoint.y[familyIndex]?.[metricIndex];
                    if (yValue !== undefined) {
                        record[`y2_${familyIndex}_${metricIndex}`] = yValue;
                    }
                }
            });
        }

        // Add z-axis (dimension) values if present
        // Store all Z values and create a series identifier from all Z values
        if (zAxisTitle && dataPoint.z && dataPoint.z.length > 0) {
            // Store all Z values
            record.zValues = dataPoint.z;
            // Create a series identifier from all Z values (e.g., "Assoc Brands - AB Inputs")
            record.zSeriesKey = dataPoint.z.join('-');
            // Keep backward compatibility with zAxisKey (lowest level)
            if (zAxisKey) {
                record[zAxisKey] = dataPoint.z[dataPoint.z.length - 1]; // Use lowest level
            }
        }

        return record;
    });

    const y1AxisLabel = resolveAxisLabelForMetricSelection(selectedMetrics?.y1, y1LineConfigs, yAxisHeaders);
    const y2AxisLabel = resolveAxisLabelForMetricSelection(selectedMetrics?.y2, y2LineConfigs, yAxisHeaders);
    const yAxisLabel = y1AxisLabel || y2AxisLabel || yAxisTitle?.[0]?.[0] || 'Value';

    return {
        data: transformedData,
        xAxisKey,
        y1LineConfigs,
        y2LineConfigs,
        title: chartTitle,
        yAxisLabel,
        y1AxisLabel,
        y2AxisLabel,
        zAxisKey,
    };
}

/**
 * Transforms viz_data format to EnhancedAreaChart format
 * @param vizData - The visualization data
 * @param selectedMetrics - Object with y1 and y2 metric IDs (format: 'familyIndex_metricIndex')
 */
export function transformVizDataToAreaChart(
    vizData: VizData,
    selectedMetrics?: { y1: MetricId[]; y2: MetricId[] },
): TransformedAreaChartData {
    const { chartTitle, yAxisTitle, xAxisType, data: rawDataUnsorted, zAxisTitle, yAxisHeaders: rawYAxisHeaders } = vizData;
    const yAxisHeaders = normalizeYAxisHeaders(rawYAxisHeaders);
    const rawData = sortRawDataForCategoryAxis(rawDataUnsorted ?? [], xAxisType);

    // Determine x-axis key (use first xAxisTitle or default to 'x')
    const xAxisKey = normalizeChartVizXAxisTitles(vizData)[0] || 'x';

    // Determine z-axis key if present
    const zAxisKey = zAxisTitle?.[0];

    // Process selected metrics for y1 and y2
    const y1AreaConfigs: Array<{ dataKey: string; color: string; label: string }> = [];
    const y2AreaConfigs: Array<{ dataKey: string; color: string; label: string }> = [];
    let colorIndex = 0;

    // Helper to parse metric ID
    const parseMetricId = (metricId: MetricId): { familyIndex: number; metricIndex: number } | null => {
        if (!metricId) return null;
        const [familyIndex, metricIndex] = metricId.split('_').map(Number);
        return { familyIndex, metricIndex };
    };

    // Process y1 metrics (array of metric IDs)
    if (selectedMetrics?.y1 && selectedMetrics.y1.length > 0) {
        selectedMetrics.y1.forEach((metricId) => {
            const parsed = parseMetricId(metricId);
            if (parsed) {
                const { familyIndex, metricIndex } = parsed;
                const metricLabel = yAxisTitle?.[familyIndex]?.[metricIndex];
                if (metricLabel !== undefined) {
                    const dataKey = `y1_${familyIndex}_${metricIndex}`;
                    y1AreaConfigs.push({
                        dataKey,
                        color: DEFAULT_COLORS[colorIndex % DEFAULT_COLORS.length],
                        label: metricLabel,
                    });
                    colorIndex++;
                }
            }
        });
    }

    // Process y2 metrics (array of metric IDs)
    if (selectedMetrics?.y2 && selectedMetrics.y2.length > 0) {
        selectedMetrics.y2.forEach((metricId) => {
            const parsed = parseMetricId(metricId);
            if (parsed) {
                const { familyIndex, metricIndex } = parsed;
                const metricLabel = yAxisTitle?.[familyIndex]?.[metricIndex];
                if (metricLabel !== undefined) {
                    const dataKey = `y2_${familyIndex}_${metricIndex}`;
                    y2AreaConfigs.push({
                        dataKey,
                        color: DEFAULT_COLORS[colorIndex % DEFAULT_COLORS.length],
                        label: metricLabel,
                    });
                    colorIndex++;
                }
            }
        });
    }

    // Transform data points
    const transformedData: Array<Record<string, any>> = rawData.map((dataPoint, index) => {
        const record: Record<string, any> = {
            // X-axis value (use first x value or create index-based key)
            [xAxisKey]: dataPoint.x[0] || `Point ${index + 1}`,
        };

        // Add y1 values (multiple metrics)
        if (selectedMetrics?.y1 && selectedMetrics.y1.length > 0) {
            selectedMetrics.y1.forEach((metricId) => {
                const parsed = parseMetricId(metricId);
                if (parsed) {
                    const { familyIndex, metricIndex } = parsed;
                    const yValue = dataPoint.y[familyIndex]?.[metricIndex];
                    if (yValue !== undefined) {
                        record[`y1_${familyIndex}_${metricIndex}`] = yValue;
                    }
                }
            });
        }

        // Add y2 values (multiple metrics)
        if (selectedMetrics?.y2 && selectedMetrics.y2.length > 0) {
            selectedMetrics.y2.forEach((metricId) => {
                const parsed = parseMetricId(metricId);
                if (parsed) {
                    const { familyIndex, metricIndex } = parsed;
                    const yValue = dataPoint.y[familyIndex]?.[metricIndex];
                    if (yValue !== undefined) {
                        record[`y2_${familyIndex}_${metricIndex}`] = yValue;
                    }
                }
            });
        }

        // Add z-axis (dimension) values if present
        // Store all Z values and create a series identifier from all Z values
        if (zAxisTitle && dataPoint.z && dataPoint.z.length > 0) {
            // Store all Z values
            record.zValues = dataPoint.z;
            // Create a series identifier from all Z values (e.g., "Assoc Brands - AB Inputs")
            record.zSeriesKey = dataPoint.z.join('-');
            // Keep backward compatibility with zAxisKey (lowest level)
            if (zAxisKey) {
                record[zAxisKey] = dataPoint.z[dataPoint.z.length - 1]; // Use lowest level
            }
        }

        return record;
    });

    const y1AxisLabel = resolveAxisLabelForMetricSelection(selectedMetrics?.y1, y1AreaConfigs, yAxisHeaders);
    const y2AxisLabel = resolveAxisLabelForMetricSelection(selectedMetrics?.y2, y2AreaConfigs, yAxisHeaders);
    const yAxisLabel = y1AxisLabel || y2AxisLabel || yAxisTitle?.[0]?.[0] || 'Value';

    return {
        data: transformedData,
        xAxisKey,
        y1AreaConfigs,
        y2AreaConfigs,
        title: chartTitle,
        yAxisLabel,
        y1AxisLabel,
        y2AxisLabel,
        zAxisKey,
    };
}

export interface TransformedBrandPriceLadderData {
    data: Array<Record<string, any>>;
    xAxisKey: string;
    salesBarConfig: {
        dataKey: string;
        color: string;
        label: string;
    };
    pricePointConfigs: Array<{
        dataKey: string;
        color: string;
        label: string;
    }>;
    title: string;
    y1AxisLabel?: string;
    y2AxisLabel?: string;
}

// Color mapping for price ladder metrics - aligned with Enhanced Bar Chart palette
const PRICE_LADDER_COLORS: Record<string, string> = {
    'Average Price': '#5B50D1', // Purple (matches Enhanced Bar Chart)
    'Max Price': '#DBC7A4', // Beige
    'Min Price': '#4ECDC4', // Teal
    'Promo Price': '#FF6B6B', // Red
    'Base Price': '#95E1D3', // Mint
};

/**
 * Transforms viz_data format to BrandPriceLadder format
 * @param vizData - The visualization data for BPL chart
 */
export function transformVizDataToBrandPriceLadder(
    vizData: BrandPriceLadderVizData,
): TransformedBrandPriceLadderData {
    const raw = vizData as any;

    const chartTitle: string = raw.chartTitle ?? '';

    // Accept both `xAxisTitle` and `xAxisTitles` (LLM sometimes sends plural); coerce string → string[]
    const xAxisTitle: string[] = toStringArray(raw.xAxisTitle ?? raw.xAxisTitles);

    // Accept both `yAxisTitle` and `yAxisTitles` (LLM sometimes sends plural)
    const yAxisTitle: string[] = toStringArray(raw.yAxisTitle ?? raw.yAxisTitles);

    // Coerce each data point's `x` from string → string[] when LLM sends it flat
    const rawData: BrandPriceLadderVizData['data'] = ((raw.data ?? []) as any[]).map((dp: any) => ({
        x: toStringArray(dp.x),
        y: Array.isArray(dp.y) ? dp.y : [],
    }));

    // Determine x-axis key (use first xAxisTitle or default to 'x')
    const xAxisKey = xAxisTitle[0] || 'x';

    // First y value (index 0) is Sales - goes to Y2 axis (right), grey bar
    const salesLabel = yAxisTitle[0] || 'Sales $';
    const salesBarConfig = {
        dataKey: 'sales',
        color: '#D3D3D3', // Light grey
        label: salesLabel,
    };

    // Remaining y values (indices 1-5) are price metrics - go to Y1 axis (left), colored points
    const pricePointConfigs: Array<{ dataKey: string; color: string; label: string }> = [];
    
    for (let i = 1; i < yAxisTitle.length; i++) {
        const label = yAxisTitle[i];
        if (label) {
            pricePointConfigs.push({
                dataKey: `price_${i}`,
                color: PRICE_LADDER_COLORS[label] || DEFAULT_COLORS[(i - 1) % DEFAULT_COLORS.length],
                label: label,
            });
        }
    }

    // Transform data points
    const transformedData: Array<Record<string, any>> = rawData.map((dataPoint, index) => {
        const record: Record<string, any> = {
            // X-axis value (use first x value or create index-based key)
            [xAxisKey]: dataPoint.x[0] || `Point ${index + 1}`,
        };

        // Add Sales value (first y value, index 0) - goes to Y2 axis
        if (dataPoint.y && dataPoint.y.length > 0) {
            record.sales = dataPoint.y[0];
        }

        // Add price metric values (remaining y values, indices 1-5) - go to Y1 axis
        for (let i = 1; i < dataPoint.y.length && i < yAxisTitle.length; i++) {
            record[`price_${i}`] = dataPoint.y[i];
        }

        return record;
    });

    // Determine y-axis labels
    const y1AxisLabel = pricePointConfigs[0]?.label || 'Price';
    const y2AxisLabel = salesLabel;

    return {
        data: transformedData,
        xAxisKey,
        salesBarConfig,
        pricePointConfigs,
        title: chartTitle,
        y1AxisLabel,
        y2AxisLabel,
    };
}
